"use client";

import React, { Component } from "react";
import { posthog, isPostHogReady } from "@/lib/posthog";
import { getCurrentUserId } from "@/lib/clientIdentity";
import { getSelectedTaskId } from "@/stores/socketService";
import { getCurrentTraceId } from "@/lib/clientTrace";
import { traceFlash } from "@/lib/flash-trace";

const SERVER_URL =
  process.env.NEXT_PUBLIC_SERVER_URL || "http://localhost:3005";

function shipToServer(payload: Record<string, unknown>) {
  try {
    fetch(`${SERVER_URL}/api/diag/react-error`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
      credentials: "omit",
    }).catch(() => {});
  } catch {
    // ignore — diagnostics must never break the page
  }
}

function shipToPostHog(error: Error, properties: Record<string, unknown>) {
  // PostHog is the queryable home for client errors. Server endpoint is the
  // backup channel since PostHog can be blocked by ad-blockers.
  if (!isPostHogReady()) return;
  try {
    posthog.captureException(error, properties);
  } catch {
    // ignore — diagnostics must never break the page
  }
}

interface Props {
  name: string;
  children: React.ReactNode;
  /** When true (default), renders nothing on error so the rest of the page
   *  keeps working. When false, throws back up so a parent can decide. */
  silent?: boolean;
}

interface State {
  hasError: boolean;
}

/**
 * DiagnosticBoundary — named error boundary for hunting React #300 loops.
 *
 * The single line in the JS error stack ("Minified React error #300...") is
 * useless without source maps. The componentStack from `errorInfo` includes
 * component names and parent chain — which is exactly what we need to know
 * WHICH subtree owns the setState-during-render loop.
 *
 * Each boundary's `name` prop tags the log line so we can see at a glance
 * which subtree threw, without ever resolving the minified stack.
 */
export class DiagnosticBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // The children this boundary wraps can be a whole content region, so the
    // swap to the fallback reads as the page vanishing (§flash-trace).
    traceFlash("boundary_fallback", this.props.name);
    // Identity travels in the body now (credentials stay 'omit').
    const userId = getCurrentUserId();
    const taskId = getSelectedTaskId?.() ?? undefined;
    const traceId = getCurrentTraceId?.() ?? undefined;
    const payload = {
      kind: "boundary",
      boundary: this.props.name,
      msg: error.message,
      stack: error.stack ?? null,
      componentStack: errorInfo.componentStack ?? null,
      url: typeof window !== "undefined" ? window.location.href : null,
      at: new Date().toISOString(),
      userId,
      taskId,
      traceId,
    };
    console.error("[DiagnosticBoundary]", this.props.name, payload);
    shipToServer(payload);
    shipToPostHog(error, {
      $exception_source: "react-boundary",
      boundary: this.props.name,
      componentStack: errorInfo.componentStack ?? null,
      user_id: userId,
      task_id: taskId,
      trace_id: traceId,
    });
  }

  render() {
    if (this.state.hasError) {
      return this.props.silent === false ? null : (
        <div className="text-3 text-fg-subtle p-2">
          [{this.props.name} unavailable]
        </div>
      );
    }
    return this.props.children;
  }
}
